import { useMemo } from 'react';
import { Badge, Box, InlineStack, Select, SelectMenuOptionSelectedIcon } from '@customerio/pluma-components/react';
import {
  ALL_VARIATIONS_VALUE,
  variationColorLabel,
  type NewsletterVariationOption,
} from './newsletter-recipients-options';

type NewsletterVariationSelectProps = {
  /** The selected newsletter's segmentable variations (never empty — the row hides the select otherwise). */
  variations: NewsletterVariationOption[];
  /** The stored template id (wire type preserved), or null for all variations. */
  value: number | string | null;
  disabled?: boolean;
  /** Fires with the picked option's value — `ALL_VARIATIONS_VALUE` for the no-narrowing entry. */
  onChange: (value: string) => void;
};

function variationText(option: NewsletterVariationOption): string {
  return option.language == null ? option.label : `${option.label} (${option.language})`;
}

type VariationOptionContentProps = {
  option: NewsletterVariationOption | undefined;
  isSelected?: boolean;
};

/** Menu option row: variation badge + subject (language) with the selected checkmark on the right. */
function VariationOptionContent({ option, isSelected }: VariationOptionContentProps) {
  if (option == null) {
    return null;
  }
  const colorLabel = variationColorLabel(option.variation);
  return (
    <InlineStack alignInline="space-between" alignBlock="center" gap="100" w="full">
      <InlineStack alignBlock="center" gap="100">
        {colorLabel != null ? <Badge color={colorLabel.color}>{colorLabel.label}</Badge> : null}
        <span>{variationText(option)}</span>
      </InlineStack>
      {isSelected === true ? <SelectMenuOptionSelectedIcon /> : null}
    </InlineStack>
  );
}

type VariationValueContentProps = {
  option: NewsletterVariationOption | undefined;
};

/** Selected-value display: the badge stays visible while the subject truncates. */
function VariationValueContent({ option }: VariationValueContentProps) {
  if (option == null) {
    return null;
  }
  const colorLabel = variationColorLabel(option.variation);
  return (
    <InlineStack alignBlock="center" gap="100" wrap="nowrap">
      {colorLabel != null ? (
        <Box flexShrink="0">
          <Badge color={colorLabel.color}>{colorLabel.label}</Badge>
        </Box>
      ) : null}
      <Box truncate>{variationText(option)}</Box>
    </InlineStack>
  );
}

/** The variation narrowing select: "All Variations" plus each segmentable variation with its A–H badge. */
export function NewsletterVariationSelect({ variations, value, disabled, onChange }: NewsletterVariationSelectProps) {
  const options: NewsletterVariationOption[] = useMemo(
    () => [{ label: 'All Variations', value: ALL_VARIATIONS_VALUE }, ...variations],
    [variations],
  );

  return (
    <Select
      data-testid="dryad-variation-select"
      ariaLabel="Variation"
      size="sm"
      options={options}
      value={value == null ? ALL_VARIATIONS_VALUE : String(value)}
      onChange={(picked: string | null) => {
        if (picked != null) {
          onChange(picked);
        }
      }}
      optionComponent={VariationOptionContent}
      valueComponent={VariationValueContent}
      isDisabled={disabled}
    />
  );
}
